import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  formatSignatureCompletionStatus,
  getSignatureCompletion,
  parseWalaSignatures,
  type SignatureCompletion,
  type WalaSignature,
} from './ics204SignatureSlots';
import { ics204HtmlToPlainText, isIcs204RichTextEmpty } from './ics204RichText';

export type WalaVersionRow = {
  id: string;
  updatedAt: string;
  updatedByUserName?: string | null;
  specialInstructions?: string | null;
  signatures?: WalaSignature[] | unknown;
  status?: string | null;
  proposedStatus?: string | null;
};

type Ics204VersionsDialogProps = {
  open: boolean;
  kind: 'all' | 'signed';
  versions: WalaVersionRow[];
  tipId?: string | null;
  onClose: () => void;
  onView: (version: WalaVersionRow) => void;
  getCompletion?: (signatures: WalaSignature[]) => SignatureCompletion;
  /** Replaces "Signed versions" for callers that freeze a version another way. */
  signedTitle?: string;
  description?: string;
  formatStatus?: (status: SignatureCompletion['status']) => string;
  /** Replaces the signature status line, for callers that are not ICS forms. */
  statusLabel?: (version: WalaVersionRow) => string;
};

const ALL_USERS_VALUE = '';

function parseDateTimeLocalBound(
  value: string,
  bound: 'start' | 'end'
): number | null {
  if (!value.trim()) return null;
  const parsed = dayjs(value);
  if (!parsed.isValid()) return null;
  return bound === 'end' ? parsed.endOf('minute').valueOf() : parsed.valueOf();
}

const Ics204VersionsDialog = ({
  open,
  kind,
  versions,
  tipId,
  onClose,
  onView,
  getCompletion = getSignatureCompletion,
  signedTitle = 'Signed versions',
  description = 'View a historical snapshot of this ICS-204. Viewing past versions is read-only.',
  formatStatus = formatSignatureCompletionStatus,
  statusLabel,
}: Ics204VersionsDialogProps) => {
  const [savedFrom, setSavedFrom] = useState('');
  const [savedTo, setSavedTo] = useState('');
  const [modifiedBy, setModifiedBy] = useState(ALL_USERS_VALUE);

  useEffect(() => {
    setSavedFrom('');
    setSavedTo('');
    setModifiedBy(ALL_USERS_VALUE);
  }, [open, kind]);

  const modifiedByOptions = useMemo(() => {
    const names = new Set<string>();
    for (const version of versions) {
      const name = version.updatedByUserName?.trim();
      if (name) names.add(name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [versions]);

  const filtersActive =
    Boolean(savedFrom.trim()) ||
    Boolean(savedTo.trim()) ||
    modifiedBy !== ALL_USERS_VALUE;

  const filtered = useMemo(() => {
    const fromMs = parseDateTimeLocalBound(savedFrom, 'start');
    const toMs = parseDateTimeLocalBound(savedTo, 'end');

    return [...versions].reverse().filter((version) => {
      const signatures = parseWalaSignatures(version.signatures);
      if (kind === 'signed') {
        if (getCompletion(signatures).status !== 'signed') {
          return false;
        }
      }

      const updatedMs = dayjs(version.updatedAt).valueOf();
      if (Number.isNaN(updatedMs)) return false;
      if (fromMs != null && updatedMs < fromMs) return false;
      if (toMs != null && updatedMs > toMs) return false;

      if (modifiedBy !== ALL_USERS_VALUE) {
        if ((version.updatedByUserName ?? '').trim() !== modifiedBy) {
          return false;
        }
      }

      return true;
    });
  }, [getCompletion, kind, modifiedBy, savedFrom, savedTo, versions]);

  const clearFilters = () => {
    setSavedFrom('');
    setSavedTo('');
    setModifiedBy(ALL_USERS_VALUE);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="dark tw:max-w-xl"
        data-testid="ics204-versions-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {kind === 'signed' ? signedTitle : 'Version history'}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="tw:flex tw:flex-col tw:gap-3 tw:border-b tw:border-[var(--border-primary)] tw:pb-3">
          <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-[auto_1fr_auto_1fr] tw:sm:items-center">
            <span className="tw:text-xs tw:font-medium tw:text-muted-foreground">
              Saved Between
            </span>
            <Input
              type="datetime-local"
              aria-label="Saved between start"
              data-testid="ics204-versions-filter-from"
              value={savedFrom}
              onChange={(event) => setSavedFrom(event.target.value)}
              className="tw:dark:bg-input/30"
            />
            <span className="tw:hidden tw:text-center tw:text-muted-foreground tw:sm:inline">
              –
            </span>
            <Input
              type="datetime-local"
              aria-label="Saved between end"
              data-testid="ics204-versions-filter-to"
              value={savedTo}
              onChange={(event) => setSavedTo(event.target.value)}
              className="tw:dark:bg-input/30"
            />
          </div>
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            <span className="tw:text-xs tw:font-medium tw:text-muted-foreground">
              Modified by
            </span>
            <Select
              value={modifiedBy === ALL_USERS_VALUE ? null : modifiedBy}
              onValueChange={(value) => setModifiedBy(value ?? ALL_USERS_VALUE)}
            >
              <SelectTrigger
                data-testid="ics204-versions-filter-modified-by"
                className="tw:min-w-[12rem] tw:flex-1"
              >
                <SelectValue placeholder="All users" />
              </SelectTrigger>
              <SelectContent
                alignItemWithTrigger={false}
                className="dark tw:bg-black tw:text-foreground"
              >
                <SelectItem value={null}>All users</SelectItem>
                {modifiedByOptions.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="ics204-versions-filter-clear"
              disabled={!filtersActive}
              onClick={clearFilters}
            >
              Clear filters
            </Button>
          </div>
        </div>

        <div className="tw:flex tw:max-h-80 tw:flex-col tw:gap-2 tw:overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="tw:text-sm tw:text-muted-foreground">
              {filtersActive
                ? 'No versions match these filters.'
                : 'No versions to show.'}
            </p>
          ) : (
            filtered.map((version) => {
              const signatures = parseWalaSignatures(version.signatures);
              const completion = getCompletion(signatures);
              const isTip = version.id === tipId;
              return (
                <div
                  key={version.id}
                  className="tw:flex tw:items-start tw:justify-between tw:gap-3 tw:rounded tw:border tw:border-[var(--border-primary)] tw:p-3"
                  data-testid={`ics204-version-row-${version.id}`}
                >
                  <div className="tw:min-w-0">
                    <div className="tw:text-sm tw:font-medium">
                      {new Date(version.updatedAt).toLocaleString()}
                      {isTip ? ' (latest)' : ''}
                    </div>
                    <div className="tw:text-xs tw:text-muted-foreground">
                      {statusLabel
                        ? statusLabel(version)
                        : formatStatus(completion.status)}
                      {version.updatedByUserName
                        ? ` · ${version.updatedByUserName}`
                        : ''}
                    </div>
                    {version.specialInstructions &&
                    !isIcs204RichTextEmpty(version.specialInstructions) ? (
                      <div className="tw:mt-1 tw:truncate tw:text-xs tw:text-muted-foreground">
                        {ics204HtmlToPlainText(version.specialInstructions)}
                      </div>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    data-testid={`ics204-version-view-${version.id}`}
                    onClick={() => onView(version)}
                  >
                    View
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Ics204VersionsDialog;
